import { apiClient } from '@/config/api';
import { Area, HydraCollection } from '@/types';

export type ProjectAreaAttributeItem = {
  id: number;
  uuid: string | null;
  name: string;
  attributeTypeCode: string | null;
  attributeTypeName: string | null;
  attributeTypeUnit: string | null;
  measurementPointId: number | null;
  device: { id: number; name: string | null; code: string | null } | null;
  box: { id: number; name: string | null } | null;
};

export type ProjectAreaAttributesResponse = {
  projectId: number;
  areaId: number;
  items: ProjectAreaAttributeItem[];
  totalItems: number;
};

export const areaService = {
  // Get all areas for a project
  async getAreasByProject(projectId: number, search?: string, light: boolean = false, parent?: string): Promise<Area[]> {
    const params: Record<string, any> = {
      'groups[]': light ? ['area:tree'] : ['area:read'],
      'project': `/api/projects/${projectId}`,
      'pagination': false,
    };

    // Add search parameter if provided
    // Use custom AreaSearchFilter which searches in name OR number
    if (search && search.trim()) {
      params['search'] = search.trim();
    }

    // Filter by parent IRI (requires Area SearchFilter with parent exact)
    if (parent && parent.trim()) {
      params['parent'] = parent.trim();
    }

    const response = await apiClient.get<HydraCollection<Area>>('/areas', {
      params,
    });

    const data = response.data;
    const areas = data['hydra:member'] || data['member'] || [];
    return areas;
  },

  // Get all areas filtered by type title
  async getAreasByTypeTitle(typeTitle: string): Promise<Area[]> {
    const response = await apiClient.get<HydraCollection<Area>>('/areas', {
      params: {
        'groups[]': ['area:read'],
        'type.title': typeTitle,
      },
    });

    const data = response.data;
    const areas = data['hydra:member'] || data['member'] || [];
    return areas;
  },

  // Get single area by ID
  async getArea(id: number): Promise<Area> {
    const response = await apiClient.get<Area>(`/areas/${id}`, {
      params: {
        'groups[]': ['area:read'],
      },
    });
    return response.data;
  },

  // Create area
  async createArea(data: Partial<Area>): Promise<Area> {
    const response = await apiClient.post('/areas', data);
    return response.data;
  },

  // Update area
  async updateArea(id: number, data: Partial<Area>): Promise<Area> {
    try {
      const response = await apiClient.put(`/areas/${id}`, data);
      // API Platform PUT returns 200 with the updated entity, or 204 No Content
      // If 204, we need to fetch the updated entity
      if (response.status === 204 || !response.data) {
        // Fetch the updated area
        const updatedArea = await this.getArea(id);
        return updatedArea;
      }
      return response.data;
    } catch (error: any) {
      console.error('Error in updateArea service:', error);
      throw error;
    }
  },

  // Delete area
  async deleteArea(id: number): Promise<void> {
    await apiClient.delete(`/areas/${id}`);
  },

  // Import areas from CSV
  async importAreas(projectId: number, file: File): Promise<{
    success: boolean;
    summary: {
      buildings: { created: number; items: Array<{ name: string; code: string }> };
      floors: { created: number; items: Array<{ name: string; code: string; parent: string }> };
      appartements: {
        created: number;
        total?: number;
        items: Array<{ name: string; code: string; number: string; parent: string }>;
        byFloor?: Record<string, { total: number; created: number; existing: number }>;
      };
      errors: string[];
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/projects/${projectId}/areas/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get areas tree (optimized - no relations loaded)
  async getAreasTree(projectId: number, parentId?: number): Promise<Area[]> {
    const params: Record<string, any> = {};
    if (parentId !== undefined) {
      params['parentId'] = parentId;
    }

    const response = await apiClient.get<HydraCollection<Area>>(`/projects/${projectId}/areas/tree`, {
      params,
    });

    const data = response.data;
    const areas = data['hydra:member'] || data['member'] || [];
    return areas;
  },

  // Attributes for an area (rooms, apartments, etc.)
  async getProjectAreaAttributes(projectId: number, areaId: number): Promise<ProjectAreaAttributesResponse> {
    const response = await apiClient.get<ProjectAreaAttributesResponse>(`/projects/${projectId}/areas/${areaId}/attributes`);
    return response.data;
  },

  // Admin-only optimized endpoint for areas listing (ROOT only)
  async getAdminAreasByProjectLight(projectId: number, search?: string): Promise<Area[]> {
    const params: Record<string, any> = {};
    if (search && search.trim()) {
      params.search = search.trim();
    }

    const response = await apiClient.get<Area[]>(`/admin/projects/${projectId}/areas`, { params });
    return response.data;
  },

  // Admin-only optimized endpoint for association page (ROOT only)
  async getAdminAreasAssociationLight(projectId: number, search?: string): Promise<Area[]> {
    const params: Record<string, any> = {};
    if (search && search.trim()) {
      params.search = search.trim();
    }

    const response = await apiClient.get<Area[]>(`/admin/projects/${projectId}/association/areas`, { params });
    return response.data;
  },
};
